import { ApiErrorPresenterService } from 'src/app/core/services/api-error-presenter.service';
import {
  EditorClose,
  editorSaved,
} from 'src/app/shared/utils/editor-close.utils';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
  Inject,
} from '@angular/core';
import {
  ObjectDefinition,
  User,
  UserGroup,
} from 'src/app/core/models/core.models';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
  DialogCloseEvent,
} from '@sui/dialog';
import {
  MassOperationType,
  NomObjectClass,
  NomObjectLifecycleStatus,
  NomObjectTypes,
} from 'src/app/core/constants';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { catchError, map, of, shareReplay, tap, Observable } from 'rxjs';
import {
  injectDeleteAlertMutation,
  injectToggleAlertModeMutation,
} from 'src/app/api/queries/alert.queries';
import { deleteExportScheduleSuccess } from 'src/app/core/state/object.actions';
import {
  injectDeleteExportScheduleMutation,
  injectToggleExportScheduleStatusMutation,
} from 'src/app/api/queries/export-schedule.queries';
import {
  selectUser,
  selectUserAccessLevelGlobalAdmin,
} from 'src/app/core/state';

import { AppState } from 'src/app/core/state/app.state';
import { ChangePasswordDialogComponent } from '../../../../shared/components/change-password-dialog/change-password-dialog.component';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { InvitationLinkDialogComponent } from '../../../dialogs/invitation-link-dialog/invitation-link-dialog.component';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { DashboardDuplicateDialogComponent } from '../../../../dashboard/dialogs/dashboard-duplicate-dialog/dashboard-duplicate-dialog.component';
import { EditorRoleDialogComponent } from 'src/app/admin/dialogs/editor-role-dialog/editor-role-dialog.component';
import { EditorUserGroupDialogComponent } from 'src/app/admin/dialogs/editor-user-group-dialog/editor-user-group-dialog.component';
import { FeedDuplicateDialogComponent } from '../../../../feed/dialogs/feed-duplicate-dialog/feed-duplicate-dialog.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ObjectEditorService } from '../../../../core/services/object-editor.service';
import { OBJECT_EDITOR_SERVICE } from '../../../../core/services/object-editor.token';
import { Role } from 'src/app/admin/admin.models';
import { RoleApiService } from 'src/app/api/services/role-api.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { UserGroupApiService } from 'src/app/api/services/user-group-api.service';
import { WorkspaceDuplicateDialogComponent } from '../../../../workspace/dialogs/workspace-duplicate-dialog/workspace-duplicate-dialog.component';
import { environment } from '@env/environment';
import { injectMassObjectOperation } from 'src/app/api/queries/object.queries';
import { injectSendInvitationMutation } from 'src/app/api/queries/user.queries';
import { injectDeleteRoleMutation } from 'src/app/api/queries/role.queries';
import { injectDeleteUserGroupMutation } from 'src/app/api/queries/user-group.queries';
import { OBJECT_SETTINGS_ACCESS_LEVEL } from 'src/app/core/object-settings-access-level.constants';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

export interface ObjectActionItem {
  label: string;
  icon: IconProp;
  action: () => void;

  separator?: false;
  disabled?: boolean;
  visible?: boolean;
  tooltip?: string;

  accessLevel?: UserAccessLevel;
  /** Hidden when the row's OBJECT_PERMISSIONS say `can_edit: false`. */
  requiresEdit?: boolean;
}

export interface ObjectActionSeparator {
  separator: boolean;
  accessLevel?: UserAccessLevel;
}

export type ObjectAction = ObjectActionItem | ObjectActionSeparator;

@Component({
  selector: 'app-object-card-dropdown',
  templateUrl: './object-card-dropdown.component.html',
  styleUrls: ['./object-card-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FaIconComponent],
})
export class ObjectCardDropdownComponent implements OnInit {
  @Input() objectId: string;
  @Input() objectClassId: NomObjectClass;
  @Input() objectTypeId: NomObjectTypes;
  @Input() lifecycleStatus: NomObjectLifecycleStatus;
  @Input() objectTitle: string;
  @Input() shouldInvite: boolean;
  @Input() rowData: any;

  /** Fires once an editor opened from this menu saved - the host refreshes its row. */
  @Output() saved = new EventEmitter<void>();
  @Output() rowDataChange = new EventEmitter<any>();

  actions = [];
  hasGlobalAdminAccess$ = this.store.select(selectUserAccessLevelGlobalAdmin);

  private readonly sendInvitationMutation = injectSendInvitationMutation();
  private readonly toggleAlertModeMutation = injectToggleAlertModeMutation();
  private readonly deleteAlertMutation = injectDeleteAlertMutation();
  private readonly toggleExportScheduleStatusMutation =
    injectToggleExportScheduleStatusMutation();
  private readonly deleteExportScheduleMutation =
    injectDeleteExportScheduleMutation();
  private readonly deleteRoleMutation = injectDeleteRoleMutation();
  private readonly deleteUserGroupMutation = injectDeleteUserGroupMutation();
  private readonly runMassObjectOperation = injectMassObjectOperation();

  userAccessLevel$ = this.store.select(selectUser).pipe(
    map(
      (u) =>
        (u?.user_access_level?.id ??
          UserAccessLevel.CLIENT_USER) as UserAccessLevel,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // NOTE: adding/removing an action or changing its accessLevel/requiresEdit
  // gates below MUST be mirrored in object-card-menu-availability.ts - the
  // cell renderer hides the hamburger through that metadata when the menu
  // would be empty.
  activeActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.settings',
      ),
      icon: ['fas', 'gear'],
      action: () => this.edit(),
      requiresEdit: true,
    },
    {
      separator: true,
    },
    {
      label: this.translateService.instant('object-card-dropdown.label.copy'),
      icon: ['fas', 'clone'],
      action: () => this.clone(),
    },
    {
      separator: true,
    },
    {
      icon: ['fas', 'archive'],
      label: this.translateService.instant(
        'object-card-dropdown.label.archive',
      ),
      action: () => this.archive(),
      requiresEdit: true,
    },
    {
      label: this.translateService.instant('object-card-dropdown.label.delete'),
      icon: ['fas', 'trash-alt'],
      action: () => this.delete(),
      requiresEdit: true,
    },
  ];

  archivedActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.unarchive',
      ),
      icon: ['fas', 'trash-can-arrow-up'],
      action: () => this.unarchive(),
      requiresEdit: true,
    },
    {
      label: this.translateService.instant('object-card-dropdown.label.delete'),
      icon: ['far', 'trash-alt'],
      action: () => this.delete(),
      requiresEdit: true,
    },
  ];

  deletedActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.restore',
      ),
      icon: ['fas', 'trash-can-arrow-up'],
      action: () => this.restore(),
      requiresEdit: true,
    },
  ];

  alertScheduleActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.toggle-execution-status',
      ),
      icon: ['fas', 'toggle-off'],
      action: () => this.toggleStatus(),
      requiresEdit: true,
    },
    {
      separator: true,
    },
    {
      label: this.translateService.instant('object-card-dropdown.label.delete'),
      icon: ['fas', 'trash-alt'],
      action: () => this.deleteAlertOrSchedule(),
      requiresEdit: true,
    },
  ];

  userActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.settings',
      ),
      icon: ['fas', 'edit'],
      action: () => this.editUser(),
      accessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER],
    },
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.change-password',
      ),
      icon: ['fas', 'key'],
      action: () => this.changePassword(),
      accessLevel: UserAccessLevel.CLIENT_ADMIN,
    },
    {
      separator: true,
    },
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.view-activity-logs',
      ),
      icon: ['fas', 'clock-rotate-left'],
      accessLevel: UserAccessLevel.CLIENT_ADMIN,
      action: () => this.viewActivityLogs(),
    },
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.view-search-logs',
      ),
      accessLevel: UserAccessLevel.CLIENT_ADMIN,
      icon: ['fas', 'search'],
      action: () => this.viewSearchLogs(),
    },
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.view-export-logs',
      ),
      accessLevel: UserAccessLevel.CLIENT_ADMIN,
      icon: ['fas', 'share'],
      action: () => this.viewExportLogs(),
    },
    {
      separator: true,
      accessLevel: UserAccessLevel.GLOBAL_ADMIN,
    },
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.app-login-v3',
      ),
      icon: ['fas', 'right-to-bracket'],
      action: () => this.openRemoteLogin('v3'),
      accessLevel: UserAccessLevel.GLOBAL_ADMIN,
    },
  ];

  groupsAndRolesActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.settings',
      ),
      icon: ['fas', 'edit'],
      action: () => this.editRoleOrGroup(),
      accessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.ROLE],
    },
    {
      separator: true,
    },
    {
      label: this.translateService.instant('object-card-dropdown.label.delete'),
      icon: ['fas', 'trash-alt'],
      action: () => this.deleteRoleOrGroup(),
      accessLevel: UserAccessLevel.SYSTEM_ADMIN,
    },
  ];

  companyActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.settings',
      ),
      icon: ['fas', 'edit'],
      action: () => this.editCompany(),
      accessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.COMPANY],
    },
  ];

  rulesActions: ObjectAction[] = [
    {
      label: this.translateService.instant(
        'object-card-dropdown.label.archive',
      ),
      icon: ['fas', 'archive'],
      action: () => {},
    },
    {
      separator: true,
    },
    {
      label: this.translateService.instant('object-card-dropdown.label.delete'),
      icon: ['fas', 'trash-alt'],
      action: () => {},
    },
  ];

  @Output() dropdownClosed = new EventEmitter<void>();

  private dialogService = inject(DialogService);
  private readonly presenter = inject(ApiErrorPresenterService);

  constructor(
    private elementRef: ElementRef,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    @Inject(OBJECT_EDITOR_SERVICE) private editService: ObjectEditorService,
    private router: Router,
    private userApi: UserApiService,
    private userGroupApi: UserGroupApiService,
    private roleApi: RoleApiService,
  ) {}

  ngOnInit() {
    // ! adjust positioning if inside a constrained container
    // ! inside the Table View
    this.adjustPositionForConstrainedContainer();

    // Add invite to user dropdown if needed
    if (this.shouldInvite) {
      const inviteItem: ObjectActionItem = {
        label: this.translateService.instant(
          'object-card-dropdown.label.invite',
        ),
        icon: ['fas', 'envelope'],
        action: () => this.inviteUser(),
      };

      const invitationLinkItem: ObjectActionItem = {
        label: this.translateService.instant(
          'object-card-dropdown.label.invitation-link',
        ),
        icon: ['fas', 'link'],
        action: () => this.generateInvitationLink(),
      };

      const newActions = [...this.userActions];
      const itemsToInsert: ObjectActionItem[] = [];

      const inviteAlreadyExists = newActions.some(
        (item) =>
          (item as ObjectActionItem).label === inviteItem.label &&
          (item as ObjectActionItem).icon?.[1] === inviteItem.icon[1],
      );
      if (!inviteAlreadyExists) itemsToInsert.push(inviteItem);

      const invitationLinkAlreadyExists = newActions.some(
        (item) =>
          (item as ObjectActionItem).label === invitationLinkItem.label &&
          (item as ObjectActionItem).icon?.[1] === invitationLinkItem.icon[1],
      );
      if (!invitationLinkAlreadyExists) itemsToInsert.push(invitationLinkItem);

      if (itemsToInsert.length) {
        newActions.splice(1, 0, ...itemsToInsert);
        this.userActions = newActions;
      }
    }

    // * filter out the clone action for workspace based on user access level
    this.hasGlobalAdminAccess$.subscribe((hasGlobalAdminAccess) => {
      if (
        this.objectClassId === NomObjectClass.WORKSPACE &&
        !hasGlobalAdminAccess
      ) {
        this.activeActions = this.activeActions.filter(
          (action) =>
            (action as ObjectActionItem).label !==
            this.translateService.instant('object-card-dropdown.label.clone'),
        );
      }
      this.updateActions();
    });
  }

  private updateActions() {
    const sharedLifecycleActions: Partial<
      Record<NomObjectLifecycleStatus, ObjectAction[]>
    > = {
      [NomObjectLifecycleStatus.ACTIVE]: this.activeActions,
      [NomObjectLifecycleStatus.ARCHIVED]: this.archivedActions,
      [NomObjectLifecycleStatus.DELETED]: this.deletedActions,
    };

    const classIdMap: Partial<
      Record<
        NomObjectClass,
        | ObjectAction[]
        | Partial<Record<NomObjectLifecycleStatus, ObjectAction[]>>
      >
    > = {
      [NomObjectClass.FEED]: sharedLifecycleActions,
      [NomObjectClass.DASHBOARD]: sharedLifecycleActions,
      [NomObjectClass.WORKSPACE]: sharedLifecycleActions,
      [NomObjectClass.ALERT]: this.alertScheduleActions,
      [NomObjectClass.EXPORT_SCHEDULE]: this.alertScheduleActions,
      [NomObjectClass.USER]: this.userActions,
      [NomObjectClass.USER_GROUP]: this.groupsAndRolesActions,
      [NomObjectClass.ROLE]: this.groupsAndRolesActions,
      [NomObjectClass.COMPANY]: this.companyActions,
      [NomObjectClass.RULE]: this.rulesActions,
    };

    const classEntry = classIdMap[this.objectClassId];

    let rawActions: ObjectAction[] = [];
    if (Array.isArray(classEntry)) {
      rawActions = classEntry;
    } else if (classEntry && typeof classEntry === 'object') {
      rawActions = classEntry[this.lifecycleStatus] ?? [];
    }

    // OBJECT_PERMISSIONS: when the row is the object itself and the backend
    // flagged it `can_edit: false`, the write actions go (view-only object).
    const row = this.rowData?.data;
    if (row && row.id === this.objectId && row.can_edit === false) {
      rawActions = rawActions.filter(
        (a) => !(a as ObjectActionItem).requiresEdit,
      );
    }

    // USER_ACCESS_LEVEL filtering
    this.userAccessLevel$
      .pipe(
        tap((userLevel) => {
          this.actions = this.filterActionsByAccess(rawActions, userLevel);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  @HostListener('document:click', ['$event', '$event.target'])
  public onClick(event: MouseEvent, targetElement: EventTarget): void {
    if (!targetElement) {
      return;
    }

    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.dropdownClosed.emit();
      this.destroySelf();
    }
  }

  /** Emits `saved` once the editor behind `close` saved (see `editorSaved`). */
  private emitWhenSaved(
    close: EditorClose | Observable<DialogCloseEvent<unknown>> | void,
  ): void {
    editorSaved(close).then((saved) => {
      if (saved) {
        this.saved.emit();
      }
    });
  }

  /**
   * Removes the component's element from the DOM.
   */
  destroySelf() {
    this.elementRef.nativeElement.remove();
  }

  private adjustPositionForConstrainedContainer() {
    setTimeout(() => {
      const dropdownElement =
        this.elementRef.nativeElement.querySelector('.dropdown-menu');
      if (!dropdownElement) return;

      const rect = dropdownElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // * check if dropdown overflows viewport or is inside a popup
      const isInPopup = !!this.elementRef.nativeElement.closest('.popup');
      const overflowsRight = rect.right > viewportWidth;
      const overflowsBottom = rect.bottom > viewportHeight;

      if (isInPopup || overflowsRight) {
        // * position to the left instead of right
        dropdownElement.style.insetInlineEnd = 'auto';
        dropdownElement.style.insetInlineStart = '0.5rem';
      }

      if (isInPopup || overflowsBottom) {
        // * position above instead of below if needed
        const spaceAbove = rect.top;
        const spaceBelow = viewportHeight - rect.bottom;

        if (spaceAbove > spaceBelow && spaceAbove > rect.height) {
          dropdownElement.style.insetBlockStart = 'auto';
          dropdownElement.style.insetBlockEnd = '50%';
          dropdownElement.style.transform = 'translateY(-1.2rem)';
        }
      }
    }, 0);
  }

  private performOperation(operation: MassOperationType) {
    const message = this.translateService.instant(
      'object-actions.msg.are-you-sure-you-want-to-run-operation-on-the-object',
      { operation },
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          const object: ObjectDefinition = {
            object_id: this.objectId,
            object_class_id: this.objectClassId,
            object_type_id: this.objectTypeId,
          };
          this.runMassObjectOperation(operation, [object]).catch(
            () => undefined,
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  archive() {
    this.performOperation('archive');
    this.destroySelf();
  }

  delete() {
    this.performOperation('delete');
    this.destroySelf();
  }

  unarchive() {
    this.performOperation('unarchive');
    this.destroySelf();
  }

  restore() {
    this.performOperation('undelete');
    this.destroySelf();
  }

  toggleStatus() {
    const dispatchMap = {
      [NomObjectClass.ALERT]: () =>
        this.toggleAlertModeMutation.mutate(this.objectId),
      [NomObjectClass.EXPORT_SCHEDULE]: () =>
        this.toggleExportScheduleStatusMutation
          .mutateAsync(this.objectId)
          .catch(() => undefined),
    };

    const dispatchFn = dispatchMap[this.objectClassId];
    if (dispatchFn) {
      dispatchFn();
      this.rowData = {
        ...this.rowData,
        data: {
          ...this.rowData.data,
          execution_status: {
            id: this.rowData.data.execution_status.id === 1 ? 2 : 1,
            title:
              this.rowData.data.execution_status.title === 'Running'
                ? 'Paused'
                : 'Running',
          },
        },
      };
      this.rowDataChange.emit(this.rowData);
    }
  }

  deleteAlertOrSchedule() {
    const dispatchMap = {
      // * no `deleteAlertSuccess` broadcast: it only decrements the *feed's*
      // * `alertsCount`, and an alert deleted from a grid row need not be in the
      // * current feed's list at all
      [NomObjectClass.ALERT]: () =>
        this.deleteAlertMutation
          .mutateAsync(this.objectId)
          .catch(() => undefined),
      [NomObjectClass.EXPORT_SCHEDULE]: () =>
        this.deleteExportScheduleMutation
          .mutateAsync(this.objectId)
          .then(() =>
            this.store.dispatch(
              deleteExportScheduleSuccess({ exportScheduleId: this.objectId }),
            ),
          )
          .catch(() => undefined),
    };

    const title = this.objectTitle;
    const message = this.translateService.instant(
      'object-actions.msg.are-you-sure-you-want-to-delete',
      { title },
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          const dispatchFn = dispatchMap[this.objectClassId];
          if (dispatchFn) dispatchFn();
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  editUser() {
    this.emitWhenSaved(this.editService.editUser(this.objectId));
    this.destroySelf();
  }

  changePassword() {
    this.userApi
      .getUser(this.objectId)
      .pipe(
        tap((user: User) => {
          this.dialogService.openTabsDialog(ChangePasswordDialogComponent, {
            data: { user },
            closeOnClick: false,
            closeOnEscape: true,
            height: '45vh',
            minHeight: 600,
            maxHeight: 600,
          });
        }),
        catchError((error) => {
          // Silent on purpose (WAP-1857): menu gating data, the menu falls back to the safe set.
          console.error('Failed to fetch user', error);
          return of(null);
        }),
      )
      .subscribe();

    this.destroySelf();
  }

  inviteUser() {
    if (!this.objectId) {
      console.error('Invalid user object');
      return;
    }

    const message = this.translateService.instant(
      'object-actions.msg.are-you-sure-you-want-to-invite-this-user',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          // No per-call callback: this dropdown is usually destroyed right after
          // the click. The admin Users grid mirrors the write from the mutation
          // cache. ! Failures are silent, as before.
          this.sendInvitationMutation.mutate(this.objectId);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  generateInvitationLink() {
    if (!this.objectId) {
      console.error('Invalid user object');
      return;
    }

    this.userApi
      .getPlatformInvitationLink(this.objectId)
      .pipe(
        tap((response) => {
          this.dialogService.openSimpleDialog(InvitationLinkDialogComponent, {
            data: { link: response.link },
            closeOnClick: false,
            closeOnEscape: true,
          });
        }),
        catchError((error) => {
          console.error('Failed to generate invitation link', error);
          this.presenter.present(error, {
            fallback: this.translateService.instant(
              'object-card.toast.invitation-link-could-not-be-generated',
            ),
          });
          return of(null);
        }),
      )
      .subscribe();

    this.destroySelf();
  }

  viewActivityLogs() {
    this.router.navigate(['admin', 'home', this.objectId, 'activity-logs']);
  }

  viewSearchLogs() {
    this.router.navigate(['admin', 'home', this.objectId, 'search-logs']);
  }

  viewExportLogs() {
    this.router.navigate(['admin', 'home', this.objectId, 'output-logs']);
  }

  editRoleOrGroup() {
    if (this.objectClassId === NomObjectClass.USER_GROUP) {
      this.userGroupApi
        .getUserGroup(this.objectId)
        .pipe(
          tap((userGroup: UserGroup) => {
            const dialogRef = this.dialogService.openTabsDialog(
              EditorUserGroupDialogComponent,
              {
                data: { userGroup },
                closeOnClick: false,
                closeOnEscape: true,
                height: '70vh',
                minHeight: 800,
                maxHeight: 1020,
              },
            );
            this.emitWhenSaved(dialogRef.closed);
          }),
          catchError((error) => {
            // Silent on purpose (WAP-1857): menu gating data, the menu falls back to the safe set.
            console.error('Failed to fetch company features', error);
            return of(null);
          }),
        )
        .subscribe();
    } else {
      this.roleApi
        .getRole(this.objectId)
        .pipe(
          tap((role: Role) => {
            const dialogRef = this.dialogService.openTabsDialog(
              EditorRoleDialogComponent,
              {
                data: { role },
                closeOnClick: false,
                closeOnEscape: true,
                height: '70vh',
                minHeight: 800,
                maxHeight: 1020,
              },
            );
            this.emitWhenSaved(dialogRef.closed);
          }),
          catchError((error) => {
            // Silent on purpose (WAP-1857): menu gating data, the menu falls back to the safe set.
            console.error('Failed to fetch company features', error);
            return of(null);
          }),
        )
        .subscribe();
    }

    this.destroySelf();
  }

  deleteRoleOrGroup() {
    // * the toasts live in the mutation hooks, which fire even though this dropdown
    // * is destroyed on click
    const dispatchMap = {
      [NomObjectClass.ROLE]: () =>
        this.deleteRoleMutation.mutate(this.objectId),
      [NomObjectClass.USER_GROUP]: () =>
        this.deleteUserGroupMutation.mutate(this.objectId),
    };

    const title = this.objectTitle;
    const message = this.translateService.instant(
      'object-actions.msg.are-you-sure-you-want-to-delete',
      { title },
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          const dispatchFn = dispatchMap[this.objectClassId];
          if (dispatchFn) dispatchFn();
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  editCompany() {
    this.emitWhenSaved(this.editService.editCompany(this.objectId));
    this.destroySelf();
  }

  /**
   * Edits the object based on its object class.
   */
  edit() {
    const editActions = {
      [NomObjectClass.FEED]: (id) => this.editService.loadFeedAndOpenPopup(id),
      [NomObjectClass.WORKSPACE]: (id) =>
        this.editService.loadWorkspaceAndOpenPopup(id),
      [NomObjectClass.DASHBOARD]: (id) =>
        this.editService.loadDashboardAndOpenPopup(id),
    };

    const editAction = editActions[this.objectClassId] ?? null;

    if (editAction) {
      editAction(this.objectId);
    } else {
      console.error(
        'Edit action not implemented for object class',
        this.objectClassId,
      );
    }
    this.destroySelf();
  }

  clone() {
    // ! here we have to show different popup for different object types
    // * FeedDuplicateDialogComponent -> only for Feeds or Dimensions
    // * WorkspaceDuplicateDialogComponent -> only for Workspaces
    if (this.objectClassId === NomObjectClass.FEED) {
      this.dialogService.openTabsDialog(FeedDuplicateDialogComponent, {
        data: {
          objectsData: [{ id: this.objectId, classId: this.objectClassId }],
          validateFormsOnInit: true,
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '50vh',
        minHeight: 800,
        maxHeight: 1020,
      });

      this.destroySelf();
    } else if (this.objectClassId === NomObjectClass.WORKSPACE) {
      this.dialogService.openTabsDialog(WorkspaceDuplicateDialogComponent, {
        data: {
          objectsData: [{ id: this.objectId, classId: this.objectClassId }],
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '50vh',
        minHeight: 800,
        maxHeight: 1020,
      });

      this.destroySelf();
    } else if (this.objectClassId === NomObjectClass.DASHBOARD) {
      this.dialogService.openTabsDialog(DashboardDuplicateDialogComponent, {
        data: {
          objectsData: [{ id: this.objectId, classId: this.objectClassId }],
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '50vh',
        minHeight: 800,
        maxHeight: 1020,
      });

      this.destroySelf();
    } else {
      console.error(
        'Clone action not implemented for object class',
        this.objectClassId,
      );
    }
  }

  private openRemoteLogin(version: 'v2' | 'v3'): void {
    if (!this.rowData || !this.rowData.data.login_token) {
      console.error('Invalid user data or missing login token');
      return;
    }
    const loginToken = encodeURIComponent(this.rowData.data.login_token);
    const base = `${environment.scheme}${environment.host}/`;

    const url =
      version === 'v2'
        ? `${base}legacy/#!/remote/${loginToken}`
        : `${base}/auth/remote/${loginToken}`;

    window.open(url, '_blank');
  }

  // Separators carry no id / key / label: two of them in one menu would share
  // the same track key and raise NG0955, so they track by position instead.
  trackByAction = (index: number, action: any) =>
    (action as ObjectActionSeparator).separator
      ? `separator-${index}`
      : (action?.id ?? action?.key ?? action?.label);

  onActionClick(action: any, event: MouseEvent) {
    event.stopPropagation();
    action?.action?.();
  }

  private hasRequiredAccess(action: ObjectAction, userLevel: number): boolean {
    const required = (action as any).accessLevel as UserAccessLevel | undefined;
    return required == null || userLevel >= required;
  }

  private normalizeSeparators(actions: ObjectAction[]): ObjectAction[] {
    // remove leading/trailing separators and duplicates
    const out: ObjectAction[] = [];
    for (const a of actions) {
      if ((a as ObjectActionSeparator).separator) {
        if (out.length === 0) continue; // leading
        if ((out[out.length - 1] as ObjectActionSeparator).separator) continue; // duplicate
      }
      out.push(a);
    }
    // trailing
    if (
      out.length &&
      (out[out.length - 1] as ObjectActionSeparator).separator
    ) {
      out.pop();
    }
    return out;
  }

  private filterActionsByAccess(
    actions: ObjectAction[],
    userLevel: UserAccessLevel,
  ): ObjectAction[] {
    const filtered = actions.filter((a) =>
      this.hasRequiredAccess(a, userLevel),
    );
    return this.normalizeSeparators(filtered);
  }
}
